/**
 * Cold-deck (cache-miss) policy — shared by the server sync-init filter and
 * the web client's warm/archived computation.
 *
 * MODEL: the device is a CACHE; the UserDo is the permanent, complete source
 * of truth. A deck is "cold"/"archived" when it hasn't been *active* (opened,
 * studied, or edited) within the threshold — its card bodies aren't kept on
 * the device, but they live on the server forever and re-download on demand.
 * "Archived" is therefore a reversible cache state, never deletion.
 *
 * The activity timestamp is maintained server-side per deck (the
 * `deckactivity\x01{deckId}` row) and bumped via `deck-activity-push`.
 */

/**
 * How long a deck stays "warm" (cached on-device) after its last activity.
 *
 * NOTE: this is the single tuning knob for the memory/freshness tradeoff.
 * Shorter = more memory saved but more decks paused from the daily due count
 * (Model A excludes archived decks by design). The activity signal covers
 * open/study/edit, so genuinely-in-use decks stay warm regardless.
 */
export const COLD_DECK_THRESHOLD_DAYS = 14;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Is a deck warm (kept on-device) given its last-activity timestamp?
 *
 * A missing or unparseable timestamp returns `true` (warm) — we NEVER hide a
 * deck we're unsure about. This is the fail-safe direction: over-retaining a
 * deck only costs memory; wrongly archiving one hides the user's cards.
 */
export function isDeckWarm(
  lastActivityISO: string | undefined | null,
  nowMs: number,
  thresholdDays: number = COLD_DECK_THRESHOLD_DAYS,
): boolean {
  if (!lastActivityISO) return true;
  const activityMs = Date.parse(lastActivityISO);
  if (Number.isNaN(activityMs)) return true;
  return nowMs - activityMs <= thresholdDays * MS_PER_DAY;
}

/** Inverse of {@link isDeckWarm} — a deck is archived (not cached) when it is
 *  not warm. Missing/unparseable activity ⇒ NOT archived (warm). */
export function isDeckArchived(
  lastActivityISO: string | undefined | null,
  nowMs: number,
  thresholdDays: number = COLD_DECK_THRESHOLD_DAYS,
): boolean {
  return !isDeckWarm(lastActivityISO, nowMs, thresholdDays);
}

/**
 * Given an activity map (`deckId -> lastActivityISO`) and a deck-id set,
 * partition into warm/archived. Decks absent from the activity map are warm
 * (fail-safe). Used by the client to compute `archivedDeckIds` from the
 * server's `deckActivity` map at sync-init.
 */
export function partitionDecksByWarmth(
  deckIds: Iterable<string>,
  activityByDeck: Record<string, string | undefined>,
  nowMs: number,
  thresholdDays: number = COLD_DECK_THRESHOLD_DAYS,
): { warm: Set<string>; archived: Set<string> } {
  const warm = new Set<string>();
  const archived = new Set<string>();
  for (const deckId of deckIds) {
    if (isDeckWarm(activityByDeck[deckId], nowMs, thresholdDays)) warm.add(deckId);
    else archived.add(deckId);
  }
  return { warm, archived };
}
